import { useAuth } from "@/context/auth-provider";
import delayPromise from "@/utils/delay-promise";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  CategoryAPI,
  MasterCategoryAPI,
  UserCategoryAPI,
} from "../services/transaction.service";
import {
  Category,
  CategoryTotal,
  TransactionType,
} from "../types/transaction.type";
import { UseFiltersParams } from "./use-filters";
import { toast } from "./use-toast";
import useTransactionTypes from "./use-transaction-types";

export const DEFAULT_TRANSACTION_TYPE = "Expense";

export interface UseCategoriesParams {
  filters?: UseFiltersParams;
  search?: string;
  enabled?: boolean;
}

const useCategories = (params: UseCategoriesParams = {}) => {
  const {
    filters: {
      customCategories = true,
      startDate = "",
      endDate = "",
      created = "",
      type = DEFAULT_TRANSACTION_TYPE,
    } = {
      customCategories: true,
      startDate: "",
      endDate: "",
      created: "",
      type: DEFAULT_TRANSACTION_TYPE,
    },
    search = "",
    enabled = true,
  } = params;
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [count, setCount] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(0);

  const { transactionTypes: types } = useTransactionTypes();

  // Prevent API calls if not authenticated
  const isAuthenticated = !!user;

  const fetchCategories = async () => {
    if (!isAuthenticated) return [];
    const response = await CategoryAPI.getAll();
    return response?.data?.results ?? [];
  };

  const fetchUserCategories = async () => {
    if (!isAuthenticated) return [];
    const response = await UserCategoryAPI.getAll();
    return response?.data?.results ?? [];
  };

  const fetchMasterCategories = async () => {
    if (!isAuthenticated) return [];
    const response = await MasterCategoryAPI.getAll();
    return response?.data?.results ?? [];
  };

  const fetchCategoryTotals = async () => {
    if (!isAuthenticated) return [];
    const response = await CategoryAPI.getTotals({
      use_master: !customCategories,
      start_date: startDate,
      end_date: endDate,
      created: created,
      type: types?.find((t) => t.name === type)?.id || "",
      search: search,
    });
    setCount(response?.data?.count ?? 0);
    setTotal(response?.data?.total ?? 0);
    return response?.data?.results ?? [];
  };

  const { data: categories, isLoading: loadingCategories } = useQuery<
    Category[]
  >(["categories"], fetchCategories, {
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && enabled,
    keepPreviousData: true,
    // initialData: MOCK_CATEGORY_DATA,
  });

  const { data: userCategories, isLoading: loadingUserCategories } = useQuery<
    Category[]
  >(["categories", "userCategories"], fetchUserCategories, {
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && enabled,
    keepPreviousData: true,
    // initialData: MOCK_USER_CATEGORY_DATA,
  });

  const { data: masterCategories, isLoading: loadingMasterCategories } =
    useQuery<Category[]>(
      ["categories", "masterCategories"],
      fetchMasterCategories,
      {
        refetchOnWindowFocus: false,
        enabled: isAuthenticated && enabled,
        keepPreviousData: true,
        // initialData: MOCK_MASTER_CATEGORY_DATA,
      }
    );

  const { data: categoryTotals, isLoading: loadingCategoryTotals } = useQuery<
    CategoryTotal[]
  >(
    [
      "categories",
      "totals",
      categories,
      customCategories,
      startDate,
      endDate,
      created,
      type,
      search,
    ],
    delayPromise(fetchCategoryTotals, 500),
    {
      refetchOnWindowFocus: false,
      enabled: isAuthenticated && enabled && !!types,
      keepPreviousData: true,
      // initialData: MOCK_CATEGORY_TOTAL_DATA,
    }
  );

  const createCategory = async (record: Partial<Category>) => {
    const response = await UserCategoryAPI.post({
      name: record.name,
      color: record.color?.toString(),
      parent: record.parent,
      user: user?.id,
    });
    return response.data;
  };

  const categoryCreateMutation = useMutation({
    mutationFn: (data: Partial<Category>) => createCategory(data),
    onError: (error, variables, context) => {
      toast({
        title: "Error creating category!",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      // console.error("Error creating category:", error, variables, context);
    },
    onSuccess(data, variables, context) {
      toast({
        title: "Category created!",
        description: "The category has been successfully created.",
        variant: "default",
      });
      // console.info("Category created successfully:", data, variables, context);
      queryClient.setQueryData<Category[]>(["categories"], (previous) => {
        if (!data) return previous ?? [];
        const existing = previous ?? [];
        if (existing.some((category) => category.id === data.id)) {
          return existing;
        }
        return [...existing, data];
      });
      queryClient.setQueryData<Category[]>(
        ["categories", "userCategories"],
        (previous) => {
          if (!data) return previous ?? [];
          const existing = previous ?? [];
          if (existing.some((category) => category.id === data.id)) {
            return existing;
          }
          return [...existing, data];
        }
      );
      queryClient.invalidateQueries("categories");
    },
  });

  const editCategory = async (
    id: string | number,
    record: Partial<Category>
  ) => {
    const response = await UserCategoryAPI.patch(id, {
      name: record.name,
      color: record.color?.toString(),
      parent: record.parent,
      user: user?.id,
    });
    return response.data;
  };

  const categoryEditMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number;
      data: Partial<Category>;
    }) => editCategory(id, data),
    onError: (error, variables, context) => {
      toast({
        title: "Error editing category!",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      // console.error("Error editing category:", error, variables, context);
    },
    onSuccess(data, variables, context) {
      toast({
        title: "Category updated!",
        description: "The category has been successfully updated.",
        variant: "default",
      });
      // console.info("Category updated successfully:", data, variables, context);
      queryClient.invalidateQueries("categories");
    },
  });

  const deleteCategory = async (id: string | number) => {
    const response = await UserCategoryAPI.delete(id.toString());
    return response.data;
  };

  const categoryDeleteMutation = useMutation({
    mutationFn: deleteCategory,
    onError: (error, variables, context) => {
      toast({
        title: "Error deleting category!",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      // console.error("Error deleting category:", error, variables, context);
    },
    onSuccess(data, variables, context) {
      toast({
        title: "Category deleted!",
        description: "The category has been successfully deleted.",
        variant: "default",
      });
      // console.info("Category deleted successfully:", data, variables, context);
      queryClient.invalidateQueries("categories");
    },
  });

  // Group categories by transaction type
  const categoriesByType = React.useMemo(() => {
    const grouped: Record<
      string,
      { type: TransactionType; categories: Category[] }
    > = {};

    categories?.forEach((category) => {
      if (category.type) {
        const typeId = category.type.id.toString();
        if (!grouped[typeId]) {
          grouped[typeId] = {
            type: category.type,
            categories: [],
          };
        }
        grouped[typeId].categories.push(category);
      }
    });

    return Object.values(grouped);
  }, [categories]);

  const masterCategoriesByType = React.useMemo(() => {
    const grouped: Record<
      string,
      { type: TransactionType; categories: Category[] }
    > = {};
    masterCategories?.forEach((category) => {
      if (category.type) {
        const typeId = category.type.id.toString();
        if (!grouped[typeId]) {
          grouped[typeId] = {
            type: category.type,
            categories: [],
          };
        }
        grouped[typeId].categories.push(category);
      }
    });
    return Object.values(grouped);
  }, [masterCategories]);

  return {
    categories,
    masterCategories,
    userCategories,
    categoryTotals,
    count,
    total,
    loadingCategories,
    loadingMasterCategories,
    loadingUserCategories,
    loadingCategoryTotals,
    categoriesByType,
    masterCategoriesByType,
    categoryCreateMutation,
    categoryEditMutation,
    categoryDeleteMutation,
  };
};
export default useCategories;
